import React, { useEffect, useState } from "react";
import { X, Play, Info } from "@phosphor-icons/react";
import ExerciseMedia, { extractYouTubeId } from "../ExerciseMedia";
import api from "../../lib/api";

/**
 * Modal "Cómo se hace" — se abre desde LiveSession cuando el atleta toca
 * el nombre del ejercicio. Muestra la foto en grande + botón directo al
 * video de YouTube, sin salir del entrenamiento.
 *
 * Como las RoutineExercise son una COPIA del ejercicio maestro (guardada al
 * momento de crear la rutina), pueden estar desactualizadas respecto al
 * catálogo — típicamente cuando el master se enriqueció con video/imagen
 * DESPUÉS de crear la rutina. Si falta `video_url` o `image_url`, buscamos
 * el ejercicio maestro por `exercise_id` y usamos esos datos.
 */
export default function ExerciseHowToModal({ exercise, onClose }) {
  const [master, setMaster] = useState(null);
  const modalRef = React.useRef(null);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    // Escuchar en el elemento del modal con capture: garantiza que el evento
    // se procese antes de que el iframe de YouTube lo consuma. Además, la
    // primera vez que el modal se abre le damos foco para que Escape ande.
    const el = modalRef.current;
    if (el) {
      el.addEventListener("keydown", onKey, true);
      el.focus?.();
    }
    return () => { el?.removeEventListener("keydown", onKey, true); };
  }, [onClose]);

  // Fetch del master si falta video o imagen y tenemos exercise_id
  useEffect(() => {
    setMaster(null);
    if (!exercise?.exercise_id) return;
    const missing = !exercise.video_url || !exercise.image_url;
    if (!missing) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/exercises/${exercise.exercise_id}`);
        if (!cancelled) setMaster(data);
      } catch { /* ejercicio maestro borrado o no accesible: ignoramos */ }
    })();
    return () => { cancelled = true; };
  }, [exercise?.exercise_id, exercise?.video_url, exercise?.image_url]);

  if (!exercise) return null;

  // Merge: la copia de la rutina prevalece, pero rellenamos huecos con el master.
  const merged = {
    ...exercise,
    image_url: exercise.image_url || master?.image_url || null,
    video_url: exercise.video_url || master?.video_url || null,
    media_type: exercise.media_type || master?.media_type || null,
  };
  const hasVideo = !!merged.video_url;
  const ytId = extractYouTubeId(merged.video_url);
  const isEmbeddable = !!ytId;
  const notes = merged.notes && String(merged.notes).trim();

  return (
    <div
      ref={modalRef}
      tabIndex={-1}
      className="fixed inset-0 z-[70] bg-black/85 flex items-center justify-center p-4 notranslate outline-none"
      translate="no"
      onClick={onClose}
      data-testid="exercise-howto-modal"
    >
      <div
        className="card-base w-full max-w-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between p-4 pb-3 border-b border-app">
          <div className="min-w-0 flex-1">
            <div className="text-[10px] uppercase tracking-widest text-muted mb-1">
              Cómo se hace
            </div>
            <h3
              className="font-display font-black uppercase text-xl sm:text-2xl tracking-tighter truncate"
              data-testid="howto-title"
            >
              {merged.exercise_name || merged.name}
            </h3>
          </div>
          <button
            onClick={onClose}
            className="text-muted hover:text-white transition-colors flex-shrink-0 ml-3"
            data-testid="howto-close"
          >
            <X size={22} weight="bold" />
          </button>
        </div>

        {/* Media: si tenemos un YouTube video ID válido, embebemos el iframe
            directamente (dedicated view = auto-load). Si no, fallback a
            ExerciseMedia (imagen + overlay VIDEO). */}
        <div className="aspect-video bg-[#0A0A0A]">
          {isEmbeddable ? (
            <iframe
              className="w-full h-full"
              src={`https://www.youtube.com/embed/${ytId}?rel=0&modestbranding=1`}
              title={merged.exercise_name || "YouTube"}
              frameBorder="0"
              allow="accelerated-sensors; autoplay; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              data-testid="howto-embed"
            />
          ) : (
            <ExerciseMedia exercise={merged} autoHover={false} />
          )}
        </div>

        {/* Body */}
        <div className="p-4 space-y-4">
          {notes && (
            <div className="bg-[#0A0A0A] border border-app p-3 flex gap-2">
              <Info size={16} weight="duotone" className="text-primary-app flex-shrink-0 mt-0.5" />
              <div className="text-sm text-muted leading-relaxed">{notes}</div>
            </div>
          )}

          {hasVideo && (
            isEmbeddable ? (
              // Embed en pantalla → el botón es secundario, para abrir en YouTube app / nueva pestaña.
              <a
                href={merged.video_url}
                target="_blank"
                rel="noreferrer"
                className="text-xs uppercase tracking-widest text-muted hover:text-primary-app text-center block transition-colors"
                data-testid="howto-video-link"
              >
                Abrir en YouTube ↗
              </a>
            ) : (
              // No embebible (ej: search URL). Mostrar CTA grande para acceder al video.
              <a
                href={merged.video_url}
                target="_blank"
                rel="noreferrer"
                className="btn-primary w-full flex items-center justify-center gap-2 !py-3"
                data-testid="howto-video-link"
              >
                <Play size={18} weight="fill" /> Ver técnica en YouTube
              </a>
            )
          )}
          {!hasVideo && (
            <div className="text-xs text-muted text-center py-2">
              No hay video cargado para este ejercicio.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
